import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import SearchAndFilter from "../../../../components/SearchAndFilter";
import { toast } from "sonner";
import { Mail, CalendarClock, Receipt, Calendar, AlertTriangle, Check, Users, DollarSign, FileText } from "lucide-react";
import { EmptyState, MetricCard, SectionHeading } from "../shared";

/**
 * SellerAutopayChip — tiny pill showing an invoice's autopay lifecycle.
 * Co-located with InvoicesTab since it's the only consumer.
 */
function SellerAutopayChip({ inv }) {
  const s = inv.autopay_status;
  if (!s || s === "manual") return null;
  const cls =
    s === "scheduled" ? "text-indigo-700" :
    s === "succeeded" ? "text-emerald-700" :
    s === "prepaid"   ? "text-emerald-700" :
    s === "failed"    ? "text-rose-700" :
                         "text-foreground/60";
  const label =
    s === "scheduled" ? "Autopay scheduled"
    : s === "succeeded" ? "Autopaid"
    : s === "failed"   ? "Autopay failed"
    : s === "prepaid"  ? "Prepaid per-order"
    : s;
  const when = s === "scheduled" && inv.autopay_scheduled_at
    ? ` · ${new Date(inv.autopay_scheduled_at).toLocaleDateString()}`
    : "";
  return (
    <span className={`text-[10.5px] mono uppercase tracking-[0.14em] inline-flex items-center gap-1 ${cls}`}
          title={label + when} data-testid={`seller-autopay-${inv.code}`}>
      <CalendarClock className="w-2.5 h-2.5"/>{label}{when}
    </span>
  );
}

/**
 * InvoicesTab — daily consolidated invoices for the seller. Each day at
 * midnight one invoice is auto-issued per buyer with shipped orders.
 * Extracted from SellerDropship.jsx in Phase 6 refactor.
 */
export default function InvoicesTab({ reload }) {
  const [invoices, setInvoices] = useState([]);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState("all");
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");
  const [emailing, setEmailing] = useState(null);

  const load = () => api.get("/dropship/invoices").then(r => setInvoices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // Buyer facet options — derived from the invoice list so only connected
  // buyers show up. Same pattern as the Daily Orders tab.
  const buyerOptions = useMemo(() => {
    const seen = new Set();
    invoices.forEach(i => i.buyer_name && seen.add(i.buyer_name));
    return [...seen].sort().map(v => ({ value: v, label: v }));
  }, [invoices]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/dropship/invoices/generate-now");
      toast.success(`Generated ${r.data.generated} invoice(s) for ${r.data.date}`);
      load(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setRunning(false); }
  };

  // ── KPI rollup across the (filtered) view ──
  const filtered = useMemo(() => {
    let xs = invoices;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter(i =>
        (i.code || "").toLowerCase().includes(q) ||
        (i.buyer_name || "").toLowerCase().includes(q),
      );
    }
    // Quick chips
    const today = new Date(); today.setHours(0,0,0,0);
    const dayMs = 86400000;
    if (filter === "this_week") {
      const since = new Date(today.getTime() - 7 * dayMs).toISOString().slice(0, 10);
      xs = xs.filter(i => (i.date || "") >= since);
    } else if (filter === "this_month") {
      const m = today.toISOString().slice(0, 7);
      xs = xs.filter(i => (i.date || "").startsWith(m));
    } else if (filter === "with_deductions") {
      xs = xs.filter(i => (i.deductions_total || 0) > 0);
    } else if (filter === "issued") {
      xs = xs.filter(i => i.status === "issued");
    } else if (filter === "paid") {
      xs = xs.filter(i => i.status === "paid");
    }
    // Facet bar
    const F = filters || {};
    if (F.total?.value != null) {
      xs = xs.filter(i => {
        const v = Number(i.total || 0);
        if (F.total.op === "gt") return v > Number(F.total.value);
        if (F.total.op === "lt") return v < Number(F.total.value);
        if (F.total.op === "between") return v >= Number(F.total.min) && v <= Number(F.total.max);
        return true;
      });
    }
    if (F.status?.values?.length) xs = xs.filter(i => F.status.values.includes(i.status));
    if (F.buyer?.values?.length)  xs = xs.filter(i => F.buyer.values.includes(i.buyer_name));
    if (F.deductions?.on != null) xs = xs.filter(i => F.deductions.on ? (i.deductions_total || 0) > 0 : !i.deductions_total);
    if (F.emailed?.on != null) xs = xs.filter(i => F.emailed.on ? !!i.emailed_at : !i.emailed_at);
    if (F.date?.value) {
      // simple "after" date filter from SearchAndFilter
      xs = xs.filter(i => (i.date || "") >= F.date.value);
    }
    return xs;
  }, [invoices, filter, filters, search]);

  const kpi = useMemo(() => ({
    count: filtered.length,
    total: filtered.reduce((s, i) => s + (i.total || 0), 0),
    deductions: filtered.reduce((s, i) => s + (i.deductions_total || 0), 0),
    emailed: filtered.filter(i => i.emailed_at).length,
  }), [filtered]);

  // ── Action handlers ─────────────────────────────────────────────────
  const downloadFile = async (id, kind, filename) => {
    try {
      const { data, headers } = await api.get(`/dropship/invoices/${id}/${kind}`, { responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || (kind === "pdf" ? "application/pdf" : "text/csv") });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(`Could not download ${kind.toUpperCase()}`); }
  };
  const emailToBuyer = async (id) => {
    setEmailing(id);
    try {
      const r = await api.post(`/dropship/invoices/${id}/email`);
      toast.success(`Sent to ${r.data.to}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Email failed"); }
    finally { setEmailing(null); }
  };

  return (
    <div className="space-y-5">
      <SectionHeading
        title="Daily auto-invoices"
        sub="One consolidated invoice per buyer per day. Wholesale + shipping − pending claims = total. Net-30 by default. Generated at 23:59 UTC; manually triggerable here."
        action={<Button onClick={runNow} disabled={running} data-testid="generate-invoices" className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4">{running ? "Generating…" : "Run today now"}</Button>}
      />

      {/* ── KPI strip ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="invoice-kpis">
        <MetricCard label="Invoices" value={kpi.count}/>
        <MetricCard label="Total billed" value={money(kpi.total)}/>
        <MetricCard label="Deductions applied" value={money(kpi.deductions)} tone={kpi.deductions > 0 ? "rose" : undefined}/>
        <MetricCard label="Emailed" value={`${kpi.emailed} / ${kpi.count}`}/>
      </div>

      {/* ── Quick chips ── */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {[
          { k: "all",            l: "All",                icon: Receipt, c: invoices.length },
          { k: "this_week",      l: "This week",          icon: Calendar, c: invoices.filter(i => { const since = new Date(Date.now() - 7 * 86400000).toISOString().slice(0,10); return (i.date || "") >= since; }).length },
          { k: "this_month",     l: "This month",         icon: Calendar, c: invoices.filter(i => (i.date || "").startsWith(new Date().toISOString().slice(0,7))).length },
          { k: "with_deductions", l: "With deductions",  icon: AlertTriangle, c: invoices.filter(i => (i.deductions_total || 0) > 0).length },
          { k: "issued",         l: "Issued",             icon: Receipt, c: invoices.filter(i => i.status === "issued").length },
          { k: "paid",           l: "Paid",               icon: Check,   c: invoices.filter(i => i.status === "paid").length },
        ].map(f => {
          const active = filter === f.k;
          return (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`invoice-chip-${f.k}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 h-9 text-sm border transition-colors ${active ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}>
              <f.icon className="w-3.5 h-3.5"/>{f.l}
              <span className={`text-[10.5px] mono px-1.5 rounded-full ${active ? "bg-background/20" : "bg-secondary"}`}>{f.c}</span>
            </button>
          );
        })}
      </div>

      {/* ── Power-user facet bar ── */}
      <SearchAndFilter
        page="seller_dropship_invoices"
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        onChange={setFilters}
        totalCount={filtered.length}
        facets={[
          { id: "buyer",   label: "Buyer",      icon: Users,      type: "multi", options: buyerOptions },
          { id: "total",   label: "Total",      icon: DollarSign, type: "numeric", numericLabel: "$" },
          { id: "date",    label: "Date after", icon: Calendar,   type: "date" },
          { id: "status",  label: "Status",     icon: FileText,   type: "multi", options: [
            { value: "issued", label: "Issued" },
            { value: "paid",   label: "Paid" },
            { value: "void",   label: "Void" },
          ]},
          { id: "deductions", label: "Has deductions", icon: AlertTriangle, type: "boolean" },
          { id: "emailed",    label: "Emailed",        icon: Mail,          type: "boolean" },
        ]}
      />

      {invoices.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices yet" body="Each day at midnight, one invoice is auto-issued for every buyer with shipped orders that day. Click 'Run today now' to generate today's manually."/>
      ) : (
        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="invoices-table">
          <div className="grid grid-cols-[1.4fr_100px_70px_110px_110px_110px_110px_120px_140px] gap-3 px-5 py-2.5 bg-secondary/50 border-b border-border text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
            <div>Invoice</div><div>Date</div><div className="text-center">Orders</div>
            <div className="text-right">Wholesale</div><div className="text-right">Shipping</div>
            <div className="text-right">Deductions</div><div className="text-right">Total</div>
            <div>Generated</div><div className="text-right">Actions</div>
          </div>
          {filtered.length === 0 && <div className="px-5 py-10 text-center text-[13px] text-foreground/50" data-testid="invoices-empty">No invoices match these filters.</div>}
          <div className="divide-y divide-border">
            {filtered.map(inv => {
              const gen = inv.created_at ? new Date(inv.created_at) : null;
              const genTxt = gen ? `${gen.toLocaleDateString()} · ${gen.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}` : "—";
              return (
                <div key={inv.id} className="grid grid-cols-[1.4fr_100px_70px_110px_110px_110px_110px_120px_140px] gap-3 px-5 py-3 items-center text-[13px]" data-testid={`invoice-row-${inv.code}`}>
                  <div>
                    <div className="font-mono text-[12px]">{inv.code}</div>
                    {inv.emailed_at && (
                      <div className="text-[10.5px] mono text-emerald-700 mt-0.5 inline-flex items-center gap-1">
                        <Mail className="w-3 h-3"/>Sent {new Date(inv.emailed_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <div className="text-[12px]">{inv.date}</div>
                  <div className="text-center mono">{inv.order_count}</div>
                  <div className="text-right">{money(inv.wholesale)}</div>
                  <div className="text-right">
                    {inv.shipping_paid_by === "seller" ? (
                      <span className="text-[11px] mono text-emerald-700" title={`Seller covers — actual cost ${money(inv.actual_shipping_cost || 0)}`}>Included</span>
                    ) : (
                      <span title={inv.shipping_paid_by === "flat_rate" ? "Flat rate per order" : "Actual carrier cost passed through"}>
                        {money(inv.shipping)}
                      </span>
                    )}
                  </div>
                  <div className="text-right text-rose-700">{inv.deductions_total ? `− ${money(inv.deductions_total)}` : <span className="text-foreground/30">—</span>}</div>
                  <div className="text-right font-semibold">
                    {money(inv.total)}
                    {inv.platform_fee_amount > 0 && (
                      inv.platform_fee_paid_by === "buyer" ? (
                        <div className="text-[10px] mono text-blue-700 mt-0.5 font-normal" title={`Buyer absorbs the ${(Number(inv.platform_fee_pct) || 0).toFixed(1)}% platform fee · You receive ${money(inv.seller_payout)}`}>
                          incl {money(inv.platform_fee_amount)} buyer fee · {money(inv.seller_payout)} to you
                        </div>
                      ) : (
                        <div className="text-[10px] mono text-foreground/55 mt-0.5 font-normal" title={`Platform fee ${(Number(inv.platform_fee_pct) || 0).toFixed(1)}% · Net to you ${money(inv.seller_payout)}`}>
                          −{money(inv.platform_fee_amount)} fee · {money(inv.seller_payout)} net
                        </div>
                      )
                    )}
                  </div>
                  <div className="text-[11.5px] text-foreground/65 flex flex-col gap-0.5">
                    <span>{genTxt}</span>
                    <SellerAutopayChip inv={inv}/>
                  </div>
                  <div className="flex justify-end gap-1">
                    <Button onClick={() => downloadFile(inv.id, "pdf", `dropship-invoice-${inv.code}.pdf`)} variant="outline" data-testid={`pdf-${inv.code}`} title="Download PDF" className="h-8 rounded-full px-2.5 text-[11px]">PDF</Button>
                    <Button onClick={() => downloadFile(inv.id, "csv", `dropship-invoice-${inv.code}.csv`)} variant="outline" data-testid={`csv-${inv.code}`} title="Download CSV" className="h-8 rounded-full px-2.5 text-[11px]">CSV</Button>
                    <Button onClick={() => emailToBuyer(inv.id)} disabled={emailing === inv.id} data-testid={`email-${inv.code}`} title="Email to buyer" className="h-8 rounded-full px-2.5 text-[11px] bg-foreground text-background hover:bg-foreground/90">
                      {emailing === inv.id ? "…" : <><Mail className="w-3 h-3 mr-1"/>Email</>}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
